
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ContactDemo from "../Dashboard/ContactDemo";
import {
  Receipt,
  Calculator,
  Boxes,
  Laptop,
  ScanBarcode,
  UserRound,
  FileCheck,
  Smartphone,
  ArrowRight,
  BarChart3,
  BriefcaseBusiness,
  Building2,
  ChartNoAxesCombined,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock3,
  CreditCard,
  FileSearch,
  Keyboard,
  LayoutDashboard,
  MenuSquare,
  MessageCircle,
  MessageSquareMore,
  PackageCheck,
  PhoneCall,
  ShoppingCart,
  Sparkles,
  Store,
  Users,
  Utensils,
  X,
} from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import CtaSection from "./CTASection";
import toast from "react-hot-toast";

import addItemsImage from "../../images/LandingPageImage/Send the bill.webp";
import gstImage from "../../images/LandingPageImage/Generate.webp";
import invoiceImage from "../../images/LandingPageImage/Apply discounts and GST.webp";
import whatsappImage from "../../images/LandingPageImage/Add items.webp";
import WhatsappMainOrdering from "../../images/LandingPageImage/POS Main_1x.webp";
import LogoScroll from "./LogoScroll";
import useContactForm from "../../services/LandingPage/useContactForm";


const benefits = [
  {
    title: "GST-ready invoices in one tap  ",
    description:
      " No manual tax calculation, and no billing mistakes at the counter.",
  },
  {
    title: "Bill sent directly on WhatsApp ",
    description:
      " Customers get a digital invoice at once — no paper, no printer, nothing to lose.",
  },
  {
    title: "Works on mobile, tablet or computer ",
    description:
      " Bill from the counter or on the move, using any device you already have.",
  },
];

const trustStats = [
  {
    title: "500+",
    label: "Businesses billing smarter with Anlook",
    icon: Building2,
  },
  {
    title: "GST-Ready",
    label: "Invoices generated with proper tax compliance",
    icon: FileCheck,
  },
  {
    title: "Real-Time",
    label: "Sales reports and business insights",
    icon: BarChart3,
  },
  {
    title: "Any Device",
    label:
      "Works on desktops, laptops, tablets, and mobiles—no new hardware required",
    icon: Laptop,
  },
];

const problems = [
  {
    description:
      "Billing mistakes and wrong totals showing up at the end of the day.",
    icon: MessageSquareMore,
  },
  {
    description:
      "No proper GST-ready invoice format, which creates avoidable compliance problems.",
    icon: Keyboard,
  },
  {
    description:
      "Long queues at checkout because billing is slow and done by hand",
    icon: Clock3,
  },
  {
    description:
      "No easy way to see daily sales, best-selling items, or remaining stock.",
    icon: FileSearch,
  },
  {
    description:
      "Paper bills that get lost, making refunds and customer questions harder to handle.",
    icon: ChartNoAxesCombined,
  },
];

const steps = [
  {
    step: "01",
    description:
      "Add items to the bill by searching, scanning a barcode, or picking from your saved product list.",
    icon: MessageCircle,
    image: addItemsImage,
  },
  {
    step: "02",
    description:
      "Discounts and GST are added automatically — the final total is calculated instantly, with no manual maths.",
    icon: MenuSquare,
    image: gstImage,
  },
  {
    step: "03",
    description: "Create a clean, GST-ready invoice with a single tap.",
    icon: CreditCard,
    image: invoiceImage,
  },
  {
    step: "04",
    description:
      "Send the bill straight to the customer's WhatsApp, or print it — whichever your counter needs.",
    icon: LayoutDashboard,
    image: whatsappImage,
  },
];

const features = [
  {
    title: "Quick Billing Screen",
    description:
      "Create a complete, accurate bill in seconds with a simple interface your staff can learn in minutes.",
    icon: Receipt,
  },
  {
    title: "GST-Compliant Invoices",
    description:
      "Tax is calculated and formatted correctly on every invoice, right from the first bill.",
    icon: FileCheck,
  },
  {
    title: "WhatsApp Invoice Delivery",
    description:
      "Send digital bills and payment reminders directly to customers on WhatsApp instead of printing paper receipts.",
    icon: MessageCircle,
  },
  {
    title: "Stock Tracking",
    description:
      "Stock levels update on their own as items are billed, so you always know what's left.",
    icon: Boxes,
  },
  {
    title: "Barcode Support",
    description:
      "Speed up billing at checkout by scanning products instead of searching by hands.",
    icon: ScanBarcode,
  },
  {
    title: "Multiple Staff and Counters ",
    description:
      "Give each staff member their own login and run billing from more than one counter at a time.",
    icon: Users,
  },
  {
    title: "Daily Sales Reports",
    description:
      "See revenue, best-selling items and payment-mode breakdowns at a glance, with no spreadsheet needed.",
    icon: BarChart3,
  },
  {
    title: "Customer Management",
    description:
      "Maintain customer records, purchase history, and contact details to provide faster service and build long-term relationships.",
    icon: UserRound,
  },
];

const reasons = [
  {
    title: "Faster checkout ",
    description:
      "Cut billing time and shorten queues during your busiest hours.",
    icon: Receipt,
  },
  {
    title: "Fewer billing mistakes ",
    description:
      "Automatic tax and total calculation removes errors before they happen.",
    icon: Calculator,
  },
  {
    title: "Simplified GST compliance ",
    description:
      "  Every invoice is created in the correct format automatically.",
    icon: FileCheck,
  },
  {
    title: "Clear view of your business  ",
    description:
      "Daily sales and stock reports are ready instantly, with no extra spreadsheet work.",
    icon: BarChart3,
  },
  {
    title: "A more modern customer experience  ",
    description:
      "Bills delivered on WhatsApp instead of paper receipts that get lost.",
    icon: Smartphone,
  },
];

const industries = [
  {
    title: "Retail Stores & Boutiques",
    description:
      "Generate fast, accurate bills for walk-in customers while managing inventory with ease.",
    icon: Store,
  },
  {
    title: "Restaurants & Cafes",
    description:
      "Speed up table and counter billing with quick order processing and seamless payments.",
    icon: Utensils,
  },
  {
    title: "Quick Service Restaurants",
    description:
      "Handle high customer turnover efficiently with instant billing and faster checkouts.",
    icon: ShoppingCart,
  },
  {
    title: "Salons & Spas",
    description:
      "Create bills for every customer visit and simplify service-based billing and payments.",
    icon: BriefcaseBusiness,
  },
  {
    title: "Grocery & Kirana Stores",
    description:
      "Process high-volume transactions quickly with barcode billing and inventory management.",
    icon: PackageCheck,
  },
  {
    title: "Multi-Outlet Businesses",
    description:
      "Manage billing, products, and sales across multiple stores from a single platform.",
    icon: Building2,
  },
];

const faqs = [
  {
    question: "Is the billing software GST-compliant?",
    answer:
      "Yes, invoices are generated in a GST-ready format with automatic tax calculation built in.",
  },
  {
    question: "Can I send the bill on WhatsApp instead of printing it?",
    answer:
      "Yes, every invoice can be sent directly to the customer's WhatsApp number as a digital bill.",
  },
  {
    question: "Does it track my inventory automatically?",
    answer:
      "Yes, stock levels update automatically each time an item is billed, with no manual entry needed.",
  },
  {
    question: "Can more than one staff member bill at the same time?",
    answer:
      "Yes, the system supports multiple user logins and multiple billing counters running together.",
  },
  {
    question: "Do I need special hardware like a barcode scanner?",
    answer:
      "No, it's optional — you can bill using search or selection alone, or add a barcode scanner for extra speed.",
  },
  {
    question: "What kind of reports do I get?",
    answer:
      "Daily and periodic sales reports, best-seller insights, and payment-mode breakdowns, all in one dashboard.",
  },
  {
    question: "Can I apply discounts or offers on a bill?",
    answer:
      "Yes, discounts can be applied at the item or bill level, and the total recalculates automatically.",
  },
  {
    question: "Will this work with my existing WhatsApp ordering setup?",
    answer:
      "Yes, billing connects with Anlook's WhatsApp ordering system so orders and invoices stay in sync.",
  },
];

const SectionHeading = ({
  badge,
  title,
  highlightedText,
  description,
  align = "center",
}) => {
  const isLeft = align === "left";

  return (
    <div className={isLeft ? "max-w-3xl" : "mx-auto max-w-3xl text-center"}>
      {badge && (
        <div
          className={`mb-5 inline-flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-4 py-2 text-sm font-semibold text-green-700 ${
            isLeft ? "" : "mx-auto"
          }`}
        >
          <Sparkles className="h-4 w-4" />
          {badge}
        </div>
      )}

      <h2 className="montserrat-font-1 text-3xl font-extrabold leading-tight tracking-tight text-slate-950 sm:text-4xl lg:text-[42px]">
        {title}{" "}
        {highlightedText && (
          <span className="montserrat-font-1 text-green-500">
            {highlightedText}
          </span>
        )}
      </h2>

      {description && (
        <p className="montserrat-font-2 mt-5 text-[16px] leading-7 text-slate-600 sm:text-lg">
          {description}
        </p>
      )}
    </div>
  );
};

const POSOrderService = () => {
  const navigate = useNavigate();
  const location = useLocation();
 const { submitRestaurantBasicInfo, getOrganizationTypes, loading } =
   useContactForm();

   const initialFormData = {
     name: "",
     email: "",
     phone: "",
     restaurantName: "",
     latitude: "",
     longitude: "",
   };


  const [openFaq, setOpenFaq] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [openDemo, setOpenDemo] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [organizationTypes, setOrganizationTypes] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveStep((previousStep) => (previousStep + 1) % steps.length);
    }, 3500);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (location.hash === "#startfree-trial") {
      setOpenDemo(true);
    }
  }, [location.hash]);

    const updateFormField = (field, value) => {
      setFormData((previous) => ({
        ...previous,
        [field]: value,
      }));
    };

      useEffect(() => {
        // if (!isOpen) {
          return undefined;
        // }

        setIsSubmitted(false);
        setShowDropdown(false);

        const handleEscape = (event) => {
          if (event.key === "Escape") {
           
          }
        };

        const previousOverflow = document.body.style.overflow;

        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", handleEscape);

        return () => {
          document.body.style.overflow = previousOverflow;
          window.removeEventListener("keydown", handleEscape);
        };
      }, []);

     useEffect(() => {
       if (!openDemo) {
         document.body.style.overflow = "";
         return undefined;
       }

       const handleEscape = (event) => {
         if (event.key === "Escape" && !loading) {
           handleClose();
         }
       };

       const previousOverflow = document.body.style.overflow;

       document.body.style.overflow = "hidden";
       window.addEventListener("keydown", handleEscape);

       return () => {
         document.body.style.overflow = previousOverflow;
         window.removeEventListener("keydown", handleEscape);
       };
     }, [openDemo, loading, location.hash]);

 const handleClose = () => {
   if (loading) return;

   setOpenDemo(false);
   setFormData(initialFormData);
   setShowDropdown(false);
   setIsSubmitted(false);

   if (location.hash === "#startfree-trial") {
     navigate("/services/pos/", {
       replace: true,
     });
   }
 };

   const handleBookDemo = async (event) => {
    event.preventDefault();

    if (loading) return;

    const cleanData = {
      name: String(formData.name || "").trim(),
      phone: String(formData.phone || "").trim(),
      email: String(formData.email || "").trim(),
      restaurantName: String(
        formData.restaurantName || "",
      ).trim(),
      latitude:
        String(formData.latitude || "").trim() ||
        "28.624711238077882",
      longitude:
        String(formData.longitude || "").trim() ||
        "77.3717769111169",
    };

    if (
      !cleanData.name ||
      !cleanData.phone ||
      !cleanData.email ||
      !cleanData.restaurantName
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    if (!/^[A-Za-z\s]+$/.test(cleanData.name)) {
      toast.error("Name should contain only letters.");
      return;
    }

    if (!/^\d{10}$/.test(cleanData.phone)) {
      toast.error(
        "Phone number must contain exactly 10 digits.",
      );
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(cleanData.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    try {
      const response =
        await submitRestaurantBasicInfo(cleanData);

    if (response?.success) {
      setFormData(initialFormData);
      setShowDropdown(false);
      setIsSubmitted(true);

      window.setTimeout(() => {
        setOpenDemo(false);
        setIsSubmitted(false);

        if (window.location.hash === "#startfree-trial") {
          navigate("/services/pos/", {
            replace: true,
          });
        }
      }, 5000);

      return;
    }

      toast.error(
        response?.message || "Unable to submit details.",
      );
    } catch (error) {
      console.error("handleBookDemo error:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong.",
      );
    }
  };

  const handleStartFreeTrial = () => {
    navigate("/services/pos/#startfree-trial");
  };

  const handleCloseDemo = () => {
    setOpenDemo(false);

    if (location.hash === "#startfree-trial") {
      navigate("/services/pos/", {
        replace: true,
      });
    }
  };

  return (
    <>
      <Navbar />

      <main className="overflow-hidden bg-white pt-[75px] text-slate-900">
        {/* HERO */}
        <section className="relative isolate montserrat-font-1">
          <div className="absolute inset-0 -z-20 bg-gradient-to-br from-green-50 via-white to-emerald-50" />

          <div className="absolute -left-32 top-20 -z-10 h-96 w-96 rounded-full bg-green-200/50 blur-3xl" />
          <div className="absolute -right-40 top-0 -z-10 h-[500px] w-[500px] rounded-full bg-green-300/30 blur-3xl" />

          <div className="mx-auto grid min-h-[calc(100vh-75px)] max-w-[1400px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.06fr_0.94fr] lg:px-20">
            <div>
              <h1 className="max-w-7xl text-[26px] font-semibold leading-snug tracking-tight text-[#212529] sm:text-[26px] md:text-[30px] lg:text-[46px]">
                GST Billing Software Built for Restaurants
              </h1>

              <p className="montserrat-font-2 mt-5 max-w-2xl text-[14px] leading-relaxed text-[#212529] sm:text-[15px] md:text-[16px]">
                Manage Billing, Inventory & WhatsApp Orders From One Dashboard
              </p>

              <p className="montserrat-font-2 mt-5 max-w-2xl text-[14px] leading-relaxed text-[#212529] sm:text-[15px] md:text-[16px]">
                ⭐⭐⭐⭐⭐ Trusted by 500+ Restaurants
              </p>

              {/* contact form  */}
              {openDemo && (
                <div
                  className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                  onMouseDown={(event) => {
                    if (event.target === event.currentTarget) {
                      handleClose();
                    }
                  }}
                >
                  <div
                    className="relative h-full max-h-[100dvh] w-full max-w-2xl overflow-y-auto rounded-none bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[30px]"
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    <div className="relative bg-gradient-to-r from-[#01C37D] to-[#00AE70] px-5 py-6 text-white sm:px-8 sm:py-7">
                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        aria-label="Close demo form"
                        className="absolute right-3 top-3 rounded-full bg-white/20 p-2 transition hover:bg-white/30 disabled:cursor-not-allowed disabled:opacity-50 sm:right-5 sm:top-5"
                      >
                        <X size={18} />
                      </button>

                      <h2 className="text-2xl font-bold sm:text-3xl">
                        {isSubmitted ? "Thank You!" : "Book a Free Demo"}
                      </h2>

                      <p className="mt-2 text-sm leading-6 text-green-50 sm:text-base">
                        {isSubmitted
                          ? "Your details have been submitted successfully."
                          : "Fill in your details and our team will get in touch shortly."}
                      </p>
                    </div>

                    {isSubmitted ? (
                      <div className="flex min-h-[390px] flex-col items-center justify-center px-5 py-10 text-center sm:px-8">
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                          <Check
                            className="h-10 w-10 text-[#01C37D]"
                            strokeWidth={3}
                          />
                        </div>

                        <h3 className="mt-6 text-2xl font-bold text-slate-900">
                          Details Submitted Successfully!
                        </h3>

                        <p className="mt-3 max-w-md text-base leading-7 text-slate-600">
                          Thank you for showing interest in Anlook.
                        </p>

                        <p className="mt-2 max-w-md text-lg font-semibold text-[#01C37D]">
                          Our team will contact you within 24 hours.
                        </p>

                        <button
                          type="button"
                          onClick={handleClose}
                          className="mt-8 h-12 min-w-[180px] rounded-xl bg-[#01C37D] px-6 font-semibold text-white shadow-md transition hover:bg-[#00AE70]"
                        >
                          Done
                        </button>
                      </div>
                    ) : (
                      <form
                        onSubmit={handleBookDemo}
                        className="space-y-5 p-5 sm:space-y-6 sm:p-8"
                      >
                        <div className="grid grid-cols-1 gap-x-5 gap-y-6 lg:grid-cols-2">
                          <div className="relative">
                            <input
                              id="name"
                              type="text"
                              placeholder=" "
                              value={formData.name}
                              disabled={loading}
                              onChange={(event) => {
                                const value = event.target.value;

                                if (/^[A-Za-z\s]*$/.test(value)) {
                                  updateFormField("name", value);
                                }
                              }}
                              className="peer h-12 w-full rounded-xl border border-slate-300 px-4 pt-5 outline-none transition focus:border-[#01C37D] focus:ring-4 focus:ring-green-100 disabled:cursor-not-allowed disabled:bg-slate-100 sm:h-14"
                            />

                            <label
                              htmlFor="name"
                              className="absolute left-4 -top-2 bg-white px-1 text-xs text-slate-500 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:-top-2 peer-focus:text-xs peer-focus:text-[#01C37D] peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs"
                            >
                              Name
                              <span className="text-red-500"> *</span>
                            </label>
                          </div>

                          <div className="relative">
                            <input
                              id="phone"
                              type="tel"
                              placeholder=" "
                              value={formData.phone}
                              maxLength={10}
                              inputMode="numeric"
                              disabled={loading}
                              onChange={(event) => {
                                const value = event.target.value.replace(
                                  /\D/g,
                                  "",
                                );

                                if (value.length <= 10) {
                                  updateFormField("phone", value);
                                }
                              }}
                              className="peer h-12 w-full rounded-xl border border-slate-300 px-4 pt-5 outline-none transition focus:border-[#01C37D] focus:ring-4 focus:ring-green-100 disabled:cursor-not-allowed disabled:bg-slate-100 sm:h-14"
                            />

                            <label
                              htmlFor="phone"
                              className="absolute left-4 -top-2 bg-white px-1 text-xs text-slate-500 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:-top-2 peer-focus:text-xs peer-focus:text-[#01C37D] peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs"
                            >
                              Phone Number
                              <span className="text-red-500"> *</span>
                            </label>
                          </div>

                          <div className="relative">
                            <input
                              id="email"
                              type="email"
                              placeholder=" "
                              value={formData.email}
                              disabled={loading}
                              onChange={(event) =>
                                updateFormField("email", event.target.value)
                              }
                              className="peer h-12 w-full rounded-xl border border-slate-300 px-4 pt-5 outline-none transition focus:border-[#01C37D] focus:ring-4 focus:ring-green-100 disabled:cursor-not-allowed disabled:bg-slate-100 sm:h-14"
                            />

                            <label
                              htmlFor="email"
                              className="absolute left-4 -top-2 bg-white px-1 text-xs text-slate-500 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:-top-2 peer-focus:text-xs peer-focus:text-[#01C37D] peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs"
                            >
                              Email
                              <span className="text-red-500"> *</span>
                            </label>
                          </div>

                          <div className="relative">
                            <button
                              type="button"
                              disabled={loading}
                              onClick={() =>
                                setShowDropdown((previous) => !previous)
                              }
                              className="flex h-12 w-full items-center justify-between rounded-xl border border-slate-300 bg-white px-4 text-left outline-none transition focus:border-[#01C37D] focus:ring-4 focus:ring-green-100 disabled:cursor-not-allowed disabled:bg-slate-100 sm:h-14"
                            >
                              <span
                                className={
                                  formData.restaurantName
                                    ? "text-slate-800"
                                    : "text-slate-400"
                                }
                              >
                                {formData.restaurantName ||
                                  "Select Business Type"}
                              </span>

                              <ChevronDown
                                size={18}
                                className={`transition-transform ${
                                  showDropdown ? "rotate-180" : ""
                                }`}
                              />
                            </button>

                            <label className="absolute left-4 -top-2 bg-white px-1 text-xs text-slate-500">
                              Business Type
                              <span className="text-red-500"> *</span>
                            </label>

                            {showDropdown && (
                              <div className="absolute left-0 top-[calc(100%+6px)] z-50 max-h-56 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl">
                                {organizationTypes.length === 0 ? (
                                  <p className="px-4 py-3 text-sm text-slate-500">
                                    No business types found
                                  </p>
                                ) : (
                                  organizationTypes.map((item, index) => {
                                    const itemId =
                                      item?.id || item?.pk_id || index;

                                    const itemName =
                                      item?.restro_type ||
                                      item?.organization_type ||
                                      item?.name ||
                                      "";

                                    return (
                                      <button
                                        key={`${itemId}-${itemName}`}
                                        type="button"
                                        onClick={() => {
                                          updateFormField(
                                            "restaurantName",
                                            itemName,
                                          );
                                          setShowDropdown(false);
                                        }}
                                        className="block w-full px-4 py-3 text-left text-sm text-slate-700 transition hover:bg-[#01C37D]/10 hover:text-[#008B59]"
                                      >
                                        {itemName}
                                      </button>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#01C37D] to-[#00AE70] text-base font-semibold text-white shadow-lg transition hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-60 sm:h-14 sm:text-lg"
                        >
                          {loading ? (
                            <>
                              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              Submit Details
                              <ArrowRight size={20} />
                            </>
                          )}
                        </button>

                        <p className="text-center text-sm text-slate-500">
                          Our team will contact you within
                          <span className="font-semibold text-[#01C37D]">
                            {" "}
                            24 hours.
                          </span>
                        </p>
                      </form>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-8 space-y-4">
                {benefits.map((benefit) => (
                  <div key={benefit.title} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
                      <Check className="h-4 w-4" strokeWidth={3} />
                    </span>

                    <p className="montserrat-font-2 text-[14px] leading-relaxed text-[#212529] sm:text-[15px] md:text-[16px]">
                      <span className="font-semibold text-[#212529]">
                        {benefit.title}
                      </span>
                      {" — "}
                      {benefit.description}
                    </p>
                  </div>
                ))}
              </div>

              <div className="montserrat-font-2 mt-10 flex flex-row gap-3 sm:flex-row">
                <button
                  onClick={handleStartFreeTrial}
                  className="flex-1 inline-flex min-h-[54px] items-center justify-center gap-2 rounded-xl bg-[#004d40] px-7 py-3.5 text-base font-bold text-white shadow-lg transition duration-300"
                >
                  Start Free Trial
                  <ArrowRight className="h-5 w-5" />
                </button>

                <button
                  onClick={handleStartFreeTrial}
                  className="flex-1 inline-flex min-h-[54px] items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-7 py-3.5 text-base font-bold text-white shadow-sm transition"
                >
                  Book a Free Demo
                  <PhoneCall className="h-5 w-5" />
                </button>
              </div>

              <div className="montserrat-font-2 mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  No credit card
                </span>

                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Same-day setup
                </span>

                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Low commission
                </span>
              </div>
            </div>

            {/* WhatsApp order mockup */}
            <div className="relative mx-auto w-full max-w-[550px]">
              {/* Background Glow */}
              <div className="absolute -inset-5 -z-10 rounded-[40px] bg-green-300/20 blur-2xl" />

              {/* Image */}
              <div className="overflow-hidden rounded-[30px] border border-green-100 bg-[#01c37d] p-3 shadow-[0_30px_90px_rgba(22,163,74,0.18)]">
                <img
                  src={WhatsappMainOrdering}
                  alt="WhatsApp Main Ordering"
                  className="w-full h-auto object-cover rounded-[30px]"
                />
              </div>
            </div>
          </div>
        </section>

        {/* TRUST BAR */}
        <section className="bg-white px-5 py-10 font-sans sm:px-8 lg:px-20 lg:py-14">
          <div className="mx-auto max-w-[1200px]">
            <div className="stats-wrap relative overflow-hidden rounded-[28px] px-4 py-6 shadow-lg sm:px-6 sm:py-8 lg:px-8 lg:py-10">
              <div className="pointer-events-none absolute -left-20 -top-24 h-56 w-56 rounded-full bg-white/10 blur-3xl" />

              <div className="pointer-events-none absolute -bottom-28 -right-16 h-64 w-64 rounded-full bg-green-600/20 blur-3xl" />

              <div className="relative grid grid-cols-2 text-white lg:grid-cols-4">
                {trustStats.map((stat, index) => {
                  const Icon = stat.icon;

                  return (
                    <div
                      key={stat.label}
                      className={`flex min-h-[125px] flex-col items-center justify-center px-3 py-4 text-center sm:min-h-[140px] sm:px-5 ${
                        index % 2 !== 0 ? "border-l border-white/20" : ""
                      } ${
                        index > 1
                          ? "border-t border-white/20 lg:border-t-0"
                          : ""
                      } ${index > 0 ? "lg:border-l lg:border-white/20" : ""}`}
                    >
                      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm sm:h-12 sm:w-12">
                        <Icon className="h-5 w-5 text-white sm:h-6 sm:w-6" />
                      </div>

                      <h3 className="montserrat-font-1 text-[24px] font-semibold leading-none tracking-normal text-white sm:text-[30px] lg:text-[30px]">
                        {stat.title}
                      </h3>

                      <p className="montserrat-font-2 mt-2 max-w-[190px] text-[13px] font-normal leading-5 text-white/85 sm:text-[14px] md:text-[16px]">
                        {stat.label}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* PROBLEM SECTION */}
        <section className="bg-white px-5 py-4 font-sans sm:px-8 lg:px-20 lg:pb-5">
          <div className="mx-auto max-w-[1200px]">
            {/* CENTER HEADING */}
            <div className="mx-auto max-w-[850px] text-center">
              <h2 className="montserrat-font-1 text-[26px] font-semibold leading-tight tracking-normal text-[#212529] sm:text-[32px] md:text-[36px] lg:text-[42px]">
                Manual WhatsApp ordering{" "}
                <span className="text-green-500">breaks down as you grow</span>
              </h2>

              <p className="montserrat-font-2 mx-auto mt-5 max-w-[760px] text-[14px] font-normal leading-relaxed text-[#212529] sm:text-[15px] md:text-[16px]">
                Billing mistakes cost money, create GST problems, and slow down
                checkout during busy hours. Many shops still use handwritten or
                spreadsheet billing with no link to stock or sales data.
              </p>
            </div>

            {/* 2 LEFT CARDS + 3 RIGHT CARDS */}
            {/* Mobile + iPad */}
            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:hidden">
              {problems.map((problem, index) => {
                const Icon = problem.icon;

                return (
                  <article
                    key={problem.title}
                    className={`group flex flex-col sm:flex-row min-h-[180px] items-start sm:items-center gap-5 rounded-[24px] border border-slate-200 bg-white p-5 sm:p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-green-300 hover:shadow-xl hover:shadow-green-100
          ${
            index === 4
              ? "md:col-span-2 md:max-w-[48%] md:w-full md:justify-self-center"
              : ""
          }`}
                  >
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-green-100 bg-green-50 text-green-500 transition duration-300 group-hover:bg-green-500 group-hover:text-white">
                      <Icon className="h-7 w-7" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="montserrat-font-1 text-[18px] font-semibold leading-snug text-[#212529]">
                        {problem.title}
                      </h3>

                      <p className="montserrat-font-2 mt-2 text-[15px] leading-relaxed text-slate-600">
                        {problem.description}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>

            {/* Desktop */}
            <div className="mt-12 hidden lg:grid gap-5">
              {/* Top Row */}
              <div className="grid gap-5 lg:grid-cols-3">
                {problems.slice(0, 3).map((problem) => {
                  const Icon = problem.icon;

                  return (
                    <article
                      key={problem.title}
                      className="group flex min-h-[180px] items-center gap-5 rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-green-300 hover:shadow-xl hover:shadow-green-100"
                    >
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-green-100 bg-green-50 text-green-500 transition duration-300 group-hover:bg-green-500 group-hover:text-white">
                        <Icon className="h-7 w-7" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="montserrat-font-1 text-[18px] font-semibold leading-snug text-[#212529]">
                          {problem.title}
                        </h3>

                        <p className="montserrat-font-2 mt-2 text-[15px] leading-relaxed text-slate-600">
                          {problem.description}
                        </p>
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* Bottom Row */}
              <div className="grid grid-cols-12 gap-5">
                {problems.slice(3, 5).map((problem, index) => {
                  const Icon = problem.icon;

                  return (
                    <article
                      key={problem.title}
                      className={`group col-span-6 ${
                        index === 0 ? "col-start-1" : "col-start-7"
                      } flex min-h-[180px] items-center gap-5 rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-green-300 hover:shadow-xl hover:shadow-green-100`}
                    >
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-green-100 bg-green-50 text-green-500 transition duration-300 group-hover:bg-green-500 group-hover:text-white">
                        <Icon className="h-7 w-7" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="montserrat-font-1 text-[18px] font-semibold leading-snug text-[#212529]">
                          {problem.title}
                        </h3>

                        <p className="montserrat-font-2 mt-2 text-[15px] leading-relaxed text-slate-600">
                          {problem.description}
                        </p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <LogoScroll />

        {/* HOW IT WORKS */}

        <section className="bg-slate-50 px-5 py-4 font-sans sm:px-8 lg:px-20 lg:py-10">
          <div className="mx-auto max-w-[1200px]">
            <SectionHeading
              title="Create and share bills"
              highlightedText="in four simple steps"
              description="Add products, calculate GST, generate an invoice, and share it directly with your customer."
            />

            <div className="mt-14 grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
              {/* LEFT SIDE IMAGE */}
              <div className="relative">
                <div className="relative overflow-hidden rounded-[28px] bg-white shadow-xl shadow-slate-200/70">
                  <div className="aspect-[4/3] w-full sm:aspect-[16/11]">
                    <img
                      key={steps[activeStep]?.image}
                      src={steps[activeStep]?.image}
                      alt={steps[activeStep]?.title}
                      className="h-full w-full animate-[stepImageFade_0.5s_ease-in-out] object-cover"
                    />
                  </div>
                </div>
              </div>

              {/* RIGHT SIDE CONTENT */}

              <div className="w-full">
                {steps.map((step, index) => {
                  const isActive = activeStep === index;

                  return (
                    <button
                      key={step.step}
                      type="button"
                      onClick={() => setActiveStep(index)}
                      className={`group relative block w-full border-b py-6 text-left transition-all duration-500 first:pt-0 ${
                        isActive
                          ? "border-green-400"
                          : "border-slate-200 hover:border-green-300"
                      }`}
                    >
                      {/* TITLE */}
                      <h3
                        className={`montserrat-font-1 text-[18px] font-semibold leading-snug transition-all duration-300 sm:text-[21px] ${
                          isActive
                            ? "text-[#212529]"
                            : "text-slate-500 group-hover:text-slate-800"
                        }`}
                      >
                        {step.title}
                      </h3>

                      {/* DESCRIPTION - ALWAYS VISIBLE */}
                      <p
                        className={`montserrat-font-2 mt-3 max-w-xl text-[14px] font-normal leading-relaxed transition-all duration-300 sm:text-[15px] ${
                          isActive
                            ? "text-slate-700"
                            : "text-slate-500 group-hover:text-slate-600"
                        }`}
                      >
                        {step.description}
                      </p>

                      {/* ACTIVE PROGRESS BORDER */}
                      {isActive && (
                        <span className="absolute -bottom-px left-0 h-[2px] w-full overflow-hidden bg-green-100">
                          <span className="block h-full animate-[stepProgress_3.5s_linear] bg-green-400" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section className="bg-white px-4 py-12 sm:px-6 sm:py-16 lg:px-20 lg:py-16">
          <div className="mx-auto max-w-7xl">
            <SectionHeading
              title="Everything required to manage"
              highlightedText="WhatsApp orders"
              description="Create your catalogue, automate conversations, collect payments and manage every order from one central platform."
            />

            <div className="mt-10 grid gap-6 sm:mt-12 md:grid-cols-2 md:gap-7 lg:mt-14 lg:grid-cols-4 lg:gap-x-8 lg:gap-y-12">
              {features.map((feature) => {
                const Icon = feature.icon;

                return (
                  <article
                    key={feature.title}
                    className="group relative overflow-hidden rounded-[24px] lg:rounded-[32px] border border-slate-200/70 bg-white/90 p-5 sm:p-6 lg:p-8 backdrop-blur-xl transition-all duration-700 ease-[cubic-bezier(.22,1,.36,1)] hover:-translate-y-3 lg:hover:-translate-y-5 hover:scale-[1.02] lg:hover:scale-[1.03] hover:border-green-300 hover:shadow-[0_25px_60px_rgba(34,197,94,0.15)] lg:hover:shadow-[0_35px_90px_rgba(34,197,94,0.18)]"
                  >
                    {/* Background Glow */}
                    <div className="absolute inset-0 opacity-0 transition-all duration-700 group-hover:opacity-100">
                      <div className="absolute -top-24 -right-20 h-56 w-56 rounded-full bg-green-300/30 blur-3xl" />
                      <div className="absolute -bottom-20 -left-16 h-44 w-44 rounded-full bg-emerald-200/30 blur-3xl" />
                    </div>

                    {/* Gradient Border */}
                    <div className="absolute inset-0 rounded-[24px] lg:rounded-[32px] border border-transparent transition-all duration-700 group-hover:border-green-300/40" />

                    {/* Decorative Circle */}
                    <div className="absolute -right-8 -top-8 h-24 w-24 lg:-right-10 lg:-top-10 lg:h-28 lg:w-28 rounded-full bg-gradient-to-br from-green-100 to-green-300 opacity-30 transition-all duration-700 group-hover:scale-150 group-hover:rotate-45" />

                    {/* Icon */}
                    <div className="relative z-20 flex h-11 w-11 lg:h-12 lg:w-12 items-center justify-center rounded-xl bg-gradient-to-br from-green-400 to-green-600 text-white shadow-xl transition-all duration-700 group-hover:-translate-y-2 group-hover:scale-110 group-hover:rotate-12">
                      <div className="absolute inset-0 rounded-xl bg-white/20 animate-pulse" />
                      <Icon className="relative z-10 h-5 w-5" />
                    </div>

                    {/* Title */}
                    <h3 className="montserrat-font-1 relative z-20 mt-6 lg:mt-8 text-xl lg:text-[22px] font-bold leading-snug text-[#212529] transition-all duration-500 group-hover:text-green-600">
                      {feature.title}
                    </h3>

                    {/* Description */}
                    <p className="montserrat-font-2 relative z-20 mt-3 lg:mt-4 text-sm sm:text-[15px] leading-6 lg:leading-7 text-slate-600 transition-all duration-500 group-hover:text-slate-700">
                      {feature.description}
                    </p>

                    {/* Bottom Divider */}
                    <div className="relative z-20 mt-6 h-px w-full bg-gradient-to-r from-transparent via-green-200 to-transparent opacity-0 transition-all duration-500 group-hover:opacity-100" />
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* WHY ANLOOK */}
        <section className="relative overflow-hidden stats-wrap px-5 py-20 text-white sm:px-8 lg:px-20 lg:py-28">
          <div className="absolute -left-24 top-10 h-80 w-80 rounded-full  blur-3xl" />
          <div className="absolute -right-20 bottom-0 h-96 w-96 rounded-full blur-3xl" />

          <div className="relative mx-auto grid max-w-[1200px] gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <h2 className="montserrat-font-1 mt-6 text-3xl font-extrabold leading-tight sm:text-4xl lg:text-[42px]">
                Built for how Indian businesses{" "}
                <span className="text-green-400">actually sell</span>
              </h2>

              <p className="montserrat-font-2 mt-6 text-base leading-7 text-white sm:text-lg">
                Anlook is designed for businesses selling over chat, on mobile,
                with customers who expect fast and personal responses.
              </p>

              <button
                onClick={handleStartFreeTrial}
                className="montserrat-font-2 mt-8 inline-flex items-center justify-center gap-2 rounded-xl bg-[#004d40] px-7 py-3.5 font-bold text-white transition"
              >
                Get Started Free
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>

            {/* RIGHT SIDE NUMBERED LIST */}
            <div className="overflow-hidden rounded-[28px]">
              {reasons.map((reason, index) => {
                const number = String(index + 1).padStart(2, "0");

                return (
                  <article
                    key={reason.title}
                    className="group flex min-h-[145px] items-center gap-5 border-b border-white/15 px-5 py-6 last:border-b-0 sm:gap-8 sm:px-8 lg:px-10"
                  >
                    {/* NUMBER */}
                    <div className="flex w-[78px] shrink-0 items-center justify-center sm:w-[110px]">
                      <span className="reason-step-number text-[52px] font-black leading-none text-transparent transition-all duration-300 sm:text-[70px]">
                        {number}
                      </span>
                    </div>

                    {/* CONTENT */}
                    <div className="min-w-0 flex-1">
                      <h3 className="montserrat-font-1 text-lg font-extrabold text-white sm:text-xl">
                        {reason.title}
                      </h3>

                      <p className="montserrat-font-2 mt-2 text-sm leading-6 text-white sm:text-base sm:leading-7">
                        {reason.description}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* WHO IT IS FOR */}
        <section className="bg-white px-5 py-20 sm:px-8 lg:px-20 lg:py-16">
          <div className="mx-auto max-w-[1200px]">
            <SectionHeading
              title="Made for businesses that sell"
              highlightedText="through conversations"
              description="Whether you sell food, products or services, Anlook gives customers an easier way to discover, order and pay."
            />

            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {industries.map((industry) => {
                const Icon = industry.icon;

                return (
                  <article
                    key={industry.title}
                    className="group rounded-3xl border border-slate-200 bg-white p-7 transition duration-300 hover:border-green-300 hover:bg-green-50/50 hover:shadow-xl hover:shadow-green-100/50"
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-green-100 text-green-600 transition group-hover:bg-green-400 group-hover:text-white">
                      <Icon className="h-7 w-7" />
                    </div>

                    <h3 className="montserrat-font-1 mt-6 text-xl font-bold text-slate-950">
                      {industry.title}
                    </h3>

                    <p className="montserrat-font-2 mt-3 leading-7 text-slate-600">
                      {industry.description}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white px-5 font-sans sm:px-8 lg:px-20">
          <div className="mx-auto max-w-[760px]">
            {/* HEADING */}
            <div className="mx-auto mb-10 max-w-[760px] text-center">
              <h2 className="montserrat-font-1 text-[26px] font-semibold leading-snug tracking-normal text-[#212529] sm:text-[32px] md:text-[36px] lg:text-[42px]">
                Frequently Asked Questions
              </h2>

              <p className="montserrat-font-2 mx-auto mt-3 max-w-[750px] text-[14px] font-normal leading-relaxed text-slate-600 sm:text-[15px] md:text-[16px]">
                Everything you need to know before launching your automated
                WhatsApp ordering experience.
              </p>
            </div>

            {/* FAQ LIST */}
            <div className="space-y-4">
              {faqs.map((faq, index) => {
                const isOpen = openFaq === index;

                return (
                  <article
                    key={faq.question}
                    className="montserrat-font-1 overflow-hidden rounded-xl bg-[#F1F5F5] transition duration-300"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? -1 : index)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left"
                    >
                      <span
                        className={`text-[16px] leading-snug text-[#0C4D45] md:text-[18px] ${
                          isOpen ? "font-semibold" : "font-normal"
                        }`}
                      >
                        {faq.question}
                      </span>

                      <ChevronDown
                        className={`h-5 w-5 shrink-0 text-[#212529] transition-transform duration-300 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    <div
                      className={`grid transition-all duration-300 ease-in-out ${
                        isOpen
                          ? "grid-rows-[1fr] opacity-100"
                          : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="montserrat-font-2 px-5 pb-5 text-[14px] font-normal leading-relaxed text-[#262728] sm:text-[15px]">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <CtaSection onStartFreeTrial={handleStartFreeTrial} />
      </main>
      <Footer />
      <ContactDemo isOpen={openDemo} onClose={handleCloseDemo} />
    </>
  );
};

export default POSOrderService;

